import Vue from 'vue'
import Router from 'vue-router'
import moment from 'moment'

import authHelper from '@/helpers/auth'
import store from '@/store'

// Views
import Login from './views/Login.vue'
import HouseList from './views/HouseList.vue'
import HouseDetails from '@/components/houselist/Details'
import DetailsInfo from '@/components/houselist/DetailsInfo'
import DetailsOwner from '@/components/houselist/DetailsOwner'
import DetailsRooms from '@/components/houselist/DetailsRooms'
import DetailsKeys from '@/components/houselist/DetailsKeys'
import DetailsReport from '@/components/houselist/DetailsReport'
import DetailsTasks from '@/components/houselist/DetailsTasks'
import DetailsRepairs from '@/components/houselist/DetailsRepairs'
import DetailsLeaseAdd from '@/components/houselist/DetailsLeaseAdd'
import LeaseBond from '@/components/houselist/details/LeaseBond'
import LeaseTenants from '@/components/houselist/details/LeaseTenants'
import Configuration from './views/Configuration.vue'
import Register from './views/Register.vue'
import ForgetPassword from './views/ForgetPassword.vue'
// import Dashboard from './views/Dashboard.vue'
import TaskLeases from '@/components/leases/Leases.vue'
import TaskRepairs from '@/components/repairs/Repairs.vue'
import Issues from '@/components/issues/Issues.vue'

Vue.use(Router)

const router = new Router({
  routes: [
    {
      path: '/',
      name: 'login',
      component: Login,
      beforeEnter: checkAndRedirect,
      meta: {
        title: 'Login',
        menuTitle: 'Login',
        menuGroup: 'login',
      },
    },
    {
      path: '/houses',
      name: 'houses',
      component: HouseList,
      beforeEnter: isLoggedIn,
      meta: {
        title: 'House List',
        menuTitle: 'Houses',
        menuGroup: 'houses',
      },
      alias: '/home',
    },
    {
      path: '/houses/:houseId',
      component: HouseDetails,
      beforeEnter: (to, from, next) => {
        if (authHelper.isAuthenticated()) {
          store.dispatch('HouseList/fetchHouse', to.params.houseId).then(() => {
            next()
          })
        } else {
          next('/')
        }
      },
      meta: {
        title: 'House Details',
      },
      children: [
        {
          path: '',
          name: 'houseDetailInfo',
          component: DetailsInfo,
          meta: {
            title: 'House Details',
            menuTitle: 'Basic Info',
            menuGroup: 'houseDetailInfo',
          },
        },
        {
          path: 'owner',
          name: 'houseDetailOwner',
          component: DetailsOwner,
          meta: {
            title: 'House Details',
            menuTitle: 'Owner',
            menuGroup: 'houseDetailInfo',
          },
        },
        {
          path: 'rooms',
          name: 'houseDetailRooms',
          component: DetailsRooms,
          meta: {
            title: 'House Details',
            menuTitle: 'Rooms',
            menuGroup: 'houseDetailInfo',
          },
        },
        {
          path: 'keys',
          name: 'houseDetailKeys',
          component: DetailsKeys,
          meta: {
            title: 'House Details',
            menuTitle: 'Keys',
            menuGroup: 'houseDetailInfo',
          },
        },
        {
          path: 'expenses',
          name: 'houseDetailFee',
          component: () => import('@/components/houselist/DetailsFee'),
          beforeEnter: (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              store.dispatch('HouseList/Fees/getAll').then(() => {
                next()
              })
            } else {
              next('/')
            }
          },
          meta: {
            title: 'House Details',
            menuTitle: 'All Expenses',
            menuGroup: 'houseDetailFee',
          },
        },
        {
          path: 'leases/add',
          name: 'houseDetailLeaseAdd',
          component: DetailsLeaseAdd,
          meta: {
            title: 'House Details',
          },
        },
        {
          path: 'leases/:id',
          component: () => import('@/components/houselist/Lease'),
          beforeEnter: (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              console.log('HouseList/Lease/getLease', {to})
              store.dispatch('HouseList/Lease/getLease', to.params.id).then(() => {
                  next()
              })
            } else {
              next('/')
            }
          },
          meta: {
            title: 'House Details',
          },
          children: [
            {
              path: '',
              name: 'houseDetailLeasesInfo',
              component: () => import('@/components/houselist/DetailsLease'),
              meta: {
                title: 'House Details',
                menuTitle: 'Payment Info',
                menuGroup: 'houseDetailLeasesInfo',
              },
            },
            {
              path: 'bond',
              name: 'houseDetailLeasesBond',
              component: LeaseBond,
              meta: {
                title: 'House Details',
                menuTitle: 'Bond Details',
                menuGroup: 'houseDetailLeasesInfo',
              },
            },
            {
              path: 'tenant',
              name: 'houseDetailLeasesTenants',
              component: LeaseTenants,
              meta: {
                title: 'House Details',
                menuTitle: 'Tenant',
                menuGroup: 'houseDetailLeasesInfo',
              },
            },
            {
              path: 'payments',
              component: () =>
                import('@/components/houselist/details/LeasePaymentsView'),
              meta: {
                title: 'House Details',
                menuTitle: 'Payments',
                menuGroup: 'houseDetailLeasesInfo',
              },
              children: [
                {
                  path: '',
                  name: 'houseDetailLeasesPayments',
                  component: () =>
                    import('@/components/houselist/details/LeasePayments'),
                  /**
                   * Hook to do pre-checks before rendering the component
                   * This will check for auth and also fetches payments
                   * data from api everytime user enters into this route
                   * unless user comes to this url straight
                   */
                  beforeEnter: (to, from, next) => {
                    if (authHelper.isAuthenticated()) {
                      if (from.name !== null) {
                        store
                          .dispatch('HouseList/Lease/getLease', to.params.id)
                          .then(() => {
                            next()
                          })
                      } else {
                        next()
                      }
                    } else {
                      next('/')
                    }
                  },
                  meta: {
                    title: 'House Details',
                    menuTitle: 'Payments',
                    menuGroup: 'houseDetailLeasesInfo',
                  },
                },
                {
                  path: ':paymentId',
                  name: 'houseDetailLeasesPaymentsDetail',
                  component: () =>
                    import('@/components/houselist/details/LeasePayment'),
                  meta: {
                    title: 'House Details',
                  },
                },
              ],
            },
          ],
        },
        {
          path: 'report',
          name: 'houseDetailReport',
          component: DetailsReport,
          meta: {
            title: 'House Details',
            menuTitle: 'All Reports',
            menuGroup: 'houseDetailReport',
          },
          beforeEnter: (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              store.dispatch('HouseList/Reports/getReports').then(() => {
                next()
              })
            } else {
              next('/')
            }
          },
        },
        {
          path: 'hhs/:id?',
          component: DetailsTasks,
          name: 'hhsTasks',
          meta: {
            title: 'House Details',
            menuTitle: 'Dates',
            menuGroup: 'hhsTasks',
          },
          beforeEnter: async (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              await store.dispatch('Tasks/fetchSelected', to.params.id)
              next()
            } else {
              next('/')
            }
          }
        },
        {
          path: 'inspections/:id?',
          component: DetailsTasks,
          name: 'inspectionTasks',
          meta: {
            title: 'House Details',
            menuTitle: 'Dates',
            menuGroup: 'inspectionTasks',
          },
          beforeEnter: async (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              // await store.dispatch('Tasks/fetchAll', { houseId: to.params.houseId, status: ['>', 2] })
              await store.dispatch('Tasks/fetchSelected', to.params.id)
              next()
            } else {
              next('/')
            }
          }
        },
        {
          path: 'repairs/:id',
          component: DetailsRepairs,
          name: 'maintenanceTasks',
          meta: {
            title: 'House Details',
            menuTitle: 'Tasks',
            menuGroup: 'maintenanceTasks',
          },
        },
      ],
    },
    {
      path: '/payments',
      component: () => import('@/components/payments/View'),
      meta: {
        title: 'Payments',
      },
      children: [
        {
          path: '',
          name: 'paymentsWeekly',
          component: () => import('@/components/payments/Weekly'),
          meta: {
            title: 'Payments',
            menuTitle: 'Weekly',
            menuGroup: 'payments',
          },
          beforeEnter: (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              store.dispatch('LeaseList/fetch').then(() => { })
              store.dispatch('Payments/getPayments', { querySearch: to.query.search })
                .then(() => next())
            } else {
              next('/')
            }
          },
        },
        {
          path: 'filter',
          name: 'paymentsFilter',
          component: () => import('@/components/payments/Filter'),
          meta: {
            title: 'Payments',
            menuTitle: 'Filter',
            menuGroup: 'payments',
          },
          // props: (route) => ({
          //   filterStartDate: route.query.start,
          //   filterEndDate: route.query.end,
          // }),
          beforeEnter: async (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              next()
            } else {
              next('/')
            }
          },
        },
        {
          path: ':paymentId',
          name: 'paymentsDetail',
          component: () => import('@/components/houselist/details/Payment'),
          props: { showSummary: true },
          meta: {
            title: 'House Details',
          },
          beforeEnter: (to, from, next) => {
            if (authHelper.isAuthenticated()) {
              store
                .dispatch('Payments/getPayment', to.params.paymentId)
                .then(() => {
                  next()
                })
            } else {
              next('/')
            }
          },
        },
      ],
    },

    {
      path: '/leases',
      name: 'Leases',
      component: TaskLeases,
      meta: {
        title: 'Lease issues',
      }
    },
    {
      path: '/repairs',
      name: 'Repairs',
      component: TaskRepairs,
      meta: {
        title: 'Repair issues',
      }
    },
    {
      path: '/issues',
      name: 'Issues',
      component: Issues,
      meta: {
        title: 'Issues',
      }
    },

    {
      path: '/Configuration',
      name: 'configuration',
      component: Configuration,
      beforeEnter: isLoggedIn,
      meta: {
        title: 'Configuration',
        menuTitle: 'Configuration',
        menuGroup: 'configuration',
      },
    },
    {
      path: '/Register',
      name: 'register',
      component: Register,
      beforeEnter: isLoggedIn,
      meta: {
        requiresAuth: true,
        title: 'Register',
        menuTitle: 'Register',
        menuGroup: 'register',
      },
    },
    {
      path: '/ForgetPassword',
      name: 'forgetPassword',
      component: ForgetPassword,
      meta: {
        requiresAuth: false,
        title: 'Forgot Password',
        menuTitle: 'Forgot Password',
        menuGroup: 'forgotPassword',
      },
    },
  ],
  mode: 'history',
})

function isLoggedIn(to, from, next) {
  if (authHelper.isAuthenticated()) {
    next()
  } else {
    next('/')
  }
}

function checkAndRedirect(to, from, next) {
  if (authHelper.isAuthenticated()) {
    next('/home')
  } else {
    next()
  }
}

export default router
